/**
 * Choosing which of a mentioned element's parameters to send (#58 item 3).
 *
 * The half of this that already existed was the half nobody could use: the
 * marker format encodes parameters, `MentionChipNode` renders them as minor
 * chips, and `mentionUtils` enriches them at send time — but there was no way to
 * *pick* any, so the chips only ever appeared on a mention pasted in from
 * somewhere else. The feature was fully wired and had no front door.
 *
 * `@Basic Wall` alone sends the model a reference. `@Basic Wall` with Fire
 * Rating and Thickness ticked sends it the two numbers the question is about,
 * which is the difference between the model reading the whole element and
 * reading what was asked.
 *
 * **The five sections are the five places a parameter can live**, and they are
 * not interchangeable: an instance parameter varies per placed element, a type
 * parameter is shared by every instance of that type, and a family parameter
 * belongs to the family behind the type. `bimgo-ai-web` separates them for that
 * reason and this keeps the split, including the two CAD ones — a linked DWG's
 * layers and blocks arrive in the same shape and are chosen the same way.
 *
 * The list is filtered rather than paged. A wall type carries dozens of
 * parameters and the name is the only thing anyone searches by.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useLocale } from "../../i18n/LocaleProvider";
import { anchorBelow, type AnchoredPanel } from "../../lib/menuAnchor";
import type { DocumentContextItem, ParameterInfo } from "../../lib/documentContext";
import type { MentionParameter, MentionParameterScope } from "../../lib/mentionUtils";

/** The panel's width, in px, so the anchor can keep it on screen. */
const PANEL_WIDTH = 320;

/** One section: where the parameters come from, and what to call that. */
interface Section {
  scope: MentionParameterScope;
  titleKey: string;
  params: ParameterInfo[];
}

/**
 * The five collections an item can carry, in the order the reference lists
 * them. A section with nothing in it is dropped rather than shown empty: a
 * wall type has no CAD layers and saying so is noise, not information.
 */
export function sectionsFor(item: DocumentContextItem | null): Section[] {
  if (!item) return [];
  const all: Section[] = [
    { scope: "instance", titleKey: "mention.params.instanceTitle", params: item.instanceParameters ?? [] },
    { scope: "type", titleKey: "mention.params.typeTitle", params: item.typeParameters ?? [] },
    { scope: "family", titleKey: "mention.params.familyTitle", params: item.familyParameters ?? [] },
    { scope: "layer", titleKey: "mention.params.cadLayerTitle", params: item.cadLayers ?? [] },
    { scope: "block", titleKey: "mention.params.cadBlockTitle", params: item.cadBlocks ?? [] },
  ];
  return all.filter((section) => section.params.length > 0);
}

/** Case-insensitive name match — the only thing anyone searches a parameter by. */
export function filterParams(params: ParameterInfo[], query: string): ParameterInfo[] {
  const needle = query.trim().toLowerCase();
  if (!needle) return params;
  return params.filter((p) => String(p.name ?? "").toLowerCase().includes(needle));
}

/**
 * Selection is keyed by id **and** scope.
 *
 * A parameter id is unique within its collection, not across them: the same
 * numeric id can appear as both an instance and a type parameter, and keying on
 * the id alone made ticking one tick the other.
 */
export function paramKey(id: string, scope: MentionParameterScope): string {
  return `${scope}:${id}`;
}

export function MentionParamPopover({
  item,
  loading,
  label,
  selected,
  onChange,
  onClose,
  anchorRect,
}: {
  item: DocumentContextItem | null;
  loading: boolean;
  label: string;
  selected: MentionParameter[];
  onChange: (next: MentionParameter[]) => void;
  onClose: () => void;
  anchorRect: { top: number; bottom: number; left: number } | null;
}) {
  const { t } = useLocale();
  const [query, setQuery] = useState("");
  const panelRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  const [style, setStyle] = useState<AnchoredPanel | null>(null);
  useEffect(() => {
    if (!anchorRect) return;
    setStyle(anchorBelow(anchorRect, { width: window.innerWidth, height: window.innerHeight }, PANEL_WIDTH));
  }, [anchorRect]);

  // The search field takes focus, not the first checkbox: the list is long and
  // typing is how anyone finds a parameter. Focusing a checkbox would also mean
  // the first space bar press ticked something.
  useEffect(() => {
    searchRef.current?.focus();
  }, []);

  // Escape closes, and so does a click anywhere else. Both are the editor's
  // expectations — the chip is inside a text field the user is still writing in.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        onClose();
      }
    };
    const onDown = (e: MouseEvent) => {
      if (!panelRef.current?.contains(e.target as Node)) onClose();
    };
    document.addEventListener("keydown", onKey, true);
    // `mousedown`, not `click`: the editor steals focus on mousedown, so waiting
    // for the full click closes the panel after the caret has already moved.
    document.addEventListener("mousedown", onDown, true);
    return () => {
      document.removeEventListener("keydown", onKey, true);
      document.removeEventListener("mousedown", onDown, true);
    };
  }, [onClose]);

  const sections = useMemo(() => sectionsFor(item), [item]);
  const chosen = useMemo(
    () => new Set(selected.map((p) => paramKey(String(p.id), p.scope ?? "instance"))),
    [selected],
  );

  const toggle = (param: ParameterInfo, scope: MentionParameterScope, on: boolean) => {
    const id = String(param.id);
    if (on) {
      onChange([...selected, { id, name: String(param.name ?? ""), scope }]);
      return;
    }
    onChange(selected.filter((p) => paramKey(String(p.id), p.scope ?? "instance") !== paramKey(id, scope)));
  };

  const visible = sections
    .map((section) => ({ ...section, params: filterParams(section.params, query) }))
    .filter((section) => section.params.length > 0);

  return (
    <div
      ref={panelRef}
      className="mention-params"
      style={{ ...(style ?? { position: "fixed", top: 0, left: 0, maxHeight: 320 }), width: PANEL_WIDTH }}
      role="dialog"
      aria-label={t("mention.params.title", { name: label })}
    >
      <div className="mention-params-head">
        <span className="mention-params-name" title={label}>
          {label}
        </span>
        {selected.length > 0 ? (
          <button type="button" className="mention-params-clear" onClick={() => onChange([])}>
            {t("mention.params.clearAll")}
          </button>
        ) : null}
      </div>

      <input
        ref={searchRef}
        className="mention-params-search"
        type="search"
        value={query}
        placeholder={t("mention.params.searchPlaceholder")}
        onChange={(e) => setQuery(e.target.value)}
      />

      <div className="mention-params-body">
        {loading ? (
          <p className="mention-params-note">{t("mention.params.loading")}</p>
        ) : sections.length === 0 ? (
          // The element carries none at all — a level, a view. Distinct from a
          // search that matched nothing, which is the reader's own doing.
          <p className="mention-params-note">{t("mention.params.empty")}</p>
        ) : visible.length === 0 ? (
          <p className="mention-params-note">{t("mention.params.searchEmpty")}</p>
        ) : (
          visible.map((section) => (
            <div key={section.scope} className="mention-params-section">
              <div className="mention-params-section-title">{t(section.titleKey)}</div>
              <ul>
                {section.params.map((param) => {
                  const id = String(param.id);
                  const on = chosen.has(paramKey(id, section.scope));
                  return (
                    <li key={id}>
                      <label className="mention-params-row">
                        <input
                          type="checkbox"
                          checked={on}
                          onChange={(e) => toggle(param, section.scope, e.target.checked)}
                        />
                        <span className="mention-params-label" title={String(param.name ?? "")}>
                          {String(param.name ?? "")}
                        </span>
                      </label>
                    </li>
                  );
                })}
              </ul>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
